import type {
  ClientErrorResponse,
  CoverageResponse,
  CreateCoverageRequest,
  UpdateCoverageRequest,
} from '@store/generated/patient-coverage';

// Re-export error type for easy access
export type { ClientErrorResponse };

/**
 * Patient coverage - represents insurance or payment information
 * Re-exported from generated API with feature-friendly name
 */
export type PatientCoverage = CoverageResponse;

/**
 * Request type for creating a new coverage
 */
export type CreatePatientCoverageRequest = CreateCoverageRequest;

/**
 * Request type for updating an existing coverage
 */
export type UpdatePatientCoverageRequest = UpdateCoverageRequest;

export type {
  PriorAuthorizationResponse as PriorAuthorizationCourse,
  PriorAuthorizationCreateRequest,
  PriorAuthorizationUpdateRequest,
} from '@store/generated/prior-authorizations';

/**
 * Coverage kind enum for type safety
 */
export type CoverageKind = 'insurance' | 'self_pay' | 'other';

/**
 * Coverage order enum for type safety
 */
export type CoverageOrder = 1 | 2 | 3; // primary, secondary, tertiary

/**
 * Relationship to subscriber options
 */
export type RelationshipToSubscriber = 'self' | 'spouse' | 'child' | 'other';

/**
 * Presentation status derived by the backend from stored status + date
 * window vs today (GDN-2411). A stored-active coverage past its
 * termination date reads as 'expired' here without any stored flip.
 */
export type CoverageDisplayStatus = 'active' | 'pending' | 'expired' | 'future' | 'cancelled';

/**
 * Helper type for organizing coverages by order
 */
export interface OrganizedCoverages {
  primary?: PatientCoverage;
  secondary?: PatientCoverage;
  tertiary?: PatientCoverage;
  /**
   * A future-dated plan waiting in the primary slot (GDN-2411): the patient
   * is switching insurance and the new plan takes over on its effective
   * date via date-of-service resolution — no user action required.
   */
  incomingPrimary?: PatientCoverage;
}

// ── Eligibility types ───────────────────────────────────────────────

import type { EligibilityCheckResponse } from '@store/generated/eligibility';

export type {
  BulkCheckRequest,
  BulkCheckResponse,
  EligibilityCheckResponse,
  GetV2ProviderEligibilityWorklistApiArg,
  ManualUpdateRequest,
  WorklistItem,
  WorklistResponse,
} from '@store/generated/eligibility';

/** Feature-friendly alias for the API response */
export type { EligibilityCheckResponse as EligibilityRecord } from '@store/generated/eligibility';
export type { PayerStatsResponse } from '@store/generated/patient-coverage';

export type StatusLight = 'GREEN' | 'YELLOW' | 'RED';

/**
 * Maps API status values (eligible/needs_review/blocked) to UI StatusLight values.
 */
export const API_STATUS_TO_LIGHT: Record<string, StatusLight> = {
  eligible: 'GREEN',
  needs_review: 'YELLOW',
  blocked: 'RED',
};

/**
 * Parsed financial fields from the nested `financials` object.
 * The API returns financials as a nested object with `_cents` suffixed keys.
 * This interface provides a flat, typed view for UI components.
 */
export interface EligibilityFinancials {
  copayment: number | null;
  coinsurance: number | null;
  deductible: number | null;
  remaining_deductible: number | null;
  oop_max: number | null;
  remaining_oop_max: number | null;
  member_obligation: number | null;
  benefit_structure: string | null;
}

/**
 * Extract typed financial fields from the raw API response.
 * Converts `_cents` values to dollars for display.
 */
export function getFinancials(data: EligibilityCheckResponse | undefined): EligibilityFinancials {
  const f = data?.financials;
  if (!f) {
    return {
      copayment: null,
      coinsurance: null,
      deductible: null,
      remaining_deductible: null,
      oop_max: null,
      remaining_oop_max: null,
      member_obligation: null,
      benefit_structure: null,
    };
  }

  const cents = (key: string): number | null => {
    const v: unknown = (f as Record<string, unknown>)[key];
    return typeof v === 'number' ? v / 100 : null;
  };

  // Coinsurance is a percent stored (per long-standing BE misnomer) under
  // `coinsurance_cents`; older manual snapshots used a bare `coinsurance`
  // key and `coinsurance_pct` never shipped. Read all three, canonical key
  // first — the same precedence as the verification form's seeding
  // (eligibility-checks/utils/verificationFinancials.ts), so the form and
  // the coverage display always agree (gdn-2457).
  const pct = (key: string): number | null => {
    const v: unknown = (f as Record<string, unknown>)[key];
    return typeof v === 'number' ? v : null;
  };

  return {
    copayment: cents('copayment_cents'),
    coinsurance: pct('coinsurance_cents') ?? pct('coinsurance') ?? pct('coinsurance_pct'),
    deductible: cents('deductible_cents'),
    remaining_deductible: cents('remaining_deductible_cents'),
    oop_max: cents('oop_max_cents'),
    remaining_oop_max: cents('remaining_oop_max_cents'),
    member_obligation: cents('member_obligation_cents'),
    benefit_structure: typeof f.benefit_structure === 'string' ? f.benefit_structure : null,
  };
}
